import styled from 'styled-components';

export const Wrapper = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  max-width: var(--maxWidth);
  margin: 0 auto;
  padding: 28px var(--gutter) 0;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

export const Feature = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: clamp(440px, 62vh, 620px);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--night);
  color: var(--on-night);
  isolation: isolate;
  animation: fadeIn 0.8s var(--ease);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background: ${({ $image }) => ($image ? `url(${$image})` : 'none')} center 25% /
      cover no-repeat;
    animation: kenburns 24s ease-out both;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
        90deg,
        rgba(18, 15, 12, 0.92) 0%,
        rgba(18, 15, 12, 0.6) 42%,
        rgba(18, 15, 12, 0) 75%
      ),
      linear-gradient(0deg, rgba(18, 15, 12, 0.85) 0%, rgba(18, 15, 12, 0) 55%);
  }

  @keyframes kenburns {
    from {
      transform: scale(1.08);
    }
    to {
      transform: scale(1);
    }
  }
`;

export const Text = styled.div`
  max-width: 620px;
  padding: clamp(24px, 5vw, 56px);
  animation: fadeUp 0.8s 0.15s var(--ease) both;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--on-night-muted);

    b {
      padding: 4px 8px;
      border-radius: 6px;
      background: var(--accent);
      color: #fff;
      font-weight: 500;
    }
  }

  h1 {
    margin: 18px 0 14px;
    font-size: clamp(40px, 6.4vw, 84px);
    font-weight: 800;
    line-height: 0.95;
    letter-spacing: -0.035em;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-bottom: 16px;
    font-family: var(--font-mono);
    font-size: 13px;
    color: var(--on-night-muted);
  }

  p {
    max-width: 520px;
    font-size: 16px;
    color: rgba(246, 241, 232, 0.86);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 26px;
    height: 50px;
    padding: 0 26px;
    border-radius: 999px;
    background: var(--on-night);
    color: var(--ink);
    font-weight: 600;
    transition: background 0.25s, color 0.25s;

    span {
      transition: transform 0.25s var(--ease);
    }

    &:hover {
      background: var(--accent);
      color: #fff;

      span {
        transform: translateX(4px);
      }
    }
  }
`;

export const UpNext = styled.aside`
  display: flex;
  flex-direction: column;
  padding: 24px;
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: var(--surface);

  h2 {
    margin-bottom: 8px;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
  }

  ol {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li + li {
    border-top: 1px solid var(--line);
  }

  a {
    display: grid;
    grid-template-columns: 26px 54px 1fr;
    align-items: center;
    gap: 14px;
    padding: 14px 0;
    animation: fadeUp 0.6s var(--ease) both;
  }

  li:nth-child(2) a { animation-delay: 80ms; }
  li:nth-child(3) a { animation-delay: 160ms; }
  li:nth-child(4) a { animation-delay: 240ms; }

  .rank {
    font-family: var(--font-display);
    font-style: italic;
    font-size: 22px;
    color: var(--muted);
  }

  img,
  .noimg {
    width: 54px;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    border-radius: 8px;
    background: var(--surface-2);
    transition: transform 0.3s var(--ease);
  }

  .info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  strong {
    font-family: var(--font-display);
    font-size: 17px;
    font-weight: 600;
    line-height: 1.15;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.2s;
  }

  small {
    margin-top: 4px;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--muted);
  }

  a:hover strong {
    color: var(--accent);
  }

  a:hover img {
    transform: scale(1.05) rotate(-2deg);
  }

  @media (max-width: 1040px) {
    ol {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 0 24px;
    }

    li + li {
      border-top: 0;
    }
  }
`;
